<template>
  <section class="what-is">
    <header>
      <h2>{{ $t('home.whatIsTitle') }}</h2>
      <p class="subtitle">{{ $t('home.whatIsDesc') }}</p>
    </header>
    <div class="what-is__visual">
      <!-- Social Icons -->
      <div class="what-is__sources">
        <div class="source-icon source-icon--fb" :class="{ active: activeSource === 0 }">
          <svg viewBox="0 0 24 24" fill="#fff" width="32" height="32">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </div>
        <div class="source-icon source-icon--ig" :class="{ active: activeSource === 1 }">
          <svg viewBox="0 0 24 24" fill="#fff" width="32" height="32">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
        </div>
        <div class="source-icon source-icon--x" :class="{ active: activeSource === 2 }">
          <svg viewBox="0 0 24 24" fill="#fff" width="32" height="32">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </div>
      </div>

      <!-- Curved Transfer Lines (Desktop: horizontal) -->
      <div class="what-is__transfer what-is__transfer--desktop">
        <svg class="transfer-curves" viewBox="0 0 200 220" fill="none" preserveAspectRatio="none">
          <path class="curve-path" d="M0,30 C60,30 140,110 200,110" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-path" d="M0,110 C60,110 140,110 200,110" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-path" d="M0,190 C60,190 140,110 200,110" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-active curve-active--1" d="M0,30 C60,30 140,110 200,110" stroke="currentColor" stroke-width="2.5"/>
          <path class="curve-active curve-active--2" d="M0,110 C60,110 140,110 200,110" stroke="currentColor" stroke-width="2.5"/>
          <path class="curve-active curve-active--3" d="M0,190 C60,190 140,110 200,110" stroke="currentColor" stroke-width="2.5"/>
        </svg>
        <div class="curve-icon curve-icon--photo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
          </svg>
        </div>
        <div class="curve-icon curve-icon--video">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><circle cx="12" cy="12" r="10"/>
          </svg>
        </div>
        <div class="curve-icon curve-icon--text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
          </svg>
        </div>
        <div class="transfer-label">
          <span>{{ $t('home.whatIsTransferLine1') }}</span>
          <span>{{ $t('home.whatIsTransferLine2') }}</span>
        </div>
      </div>

      <!-- Vertical Transfer Lines (Mobile only) -->
      <div class="what-is__transfer what-is__transfer--mobile">
        <svg class="transfer-curves-v" viewBox="0 0 220 100" fill="none">
          <path class="curve-path" d="M30,0 C30,40 110,60 110,100" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-path" d="M110,0 C110,40 110,60 110,100" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-path" d="M190,0 C190,40 110,60 110,100" stroke="#e5e7eb" stroke-width="2"/>
          <path class="curve-active-v curve-active-v--1" d="M30,0 C30,40 110,60 110,100" stroke="currentColor" stroke-width="2.5"/>
          <path class="curve-active-v curve-active-v--2" d="M110,0 C110,40 110,60 110,100" stroke="currentColor" stroke-width="2.5"/>
          <path class="curve-active-v curve-active-v--3" d="M190,0 C190,40 110,60 110,100" stroke="currentColor" stroke-width="2.5"/>
        </svg>
        <div class="curve-icon-v curve-icon-v--photo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
          </svg>
        </div>
        <div class="curve-icon-v curve-icon-v--video">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><circle cx="12" cy="12" r="10"/>
          </svg>
        </div>
        <div class="curve-icon-v curve-icon-v--text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
          </svg>
        </div>
        <div class="transfer-label">
          <span>{{ $t('home.whatIsTransferLine1') }}</span>
          <span>{{ $t('home.whatIsTransferLine2') }}</span>
        </div>
      </div>

      <!-- Open Book -->
      <div class="what-is__book">
        <div class="open-book">
          <!-- Left Page -->
          <div class="open-book__page open-book__page--left">
            <div class="page-photo page-photo--main">
              <img v-lazy="photoMain" alt="">
            </div>
            <div class="page-text-lines">
              <span></span>
              <span></span>
              <span class="short"></span>
            </div>
          </div>
          <!-- Spine -->
          <div class="open-book__spine"></div>
          <!-- Right Page -->
          <div class="open-book__page open-book__page--right">
            <div class="page-grid">
              <div class="page-photo page-photo--grid">
                <img v-lazy="photoGrid1" alt="">
              </div>
              <div class="page-photo page-photo--grid">
                <img v-lazy="photoGrid2" alt="">
              </div>
              <div class="page-photo page-photo--grid">
                <img v-lazy="photoGrid3" alt="">
              </div>
              <div class="page-photo page-photo--grid">
                <img v-lazy="photoGrid4" alt="">
              </div>
            </div>
            <div class="page-text-lines">
              <span></span>
              <span class="short"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="what-is__cta">
      <button class="btn-create" @click="showModal = true">{{ $t('home.createMyBook') }} <span class="cta-arrow"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></span></button>
    </div>

    <!-- Social Source Modal -->
    <div class="connect-modal-mask" v-if="showModal" @click.self="showModal = false">
      <div class="connect-modal">
        <div class="connect-modal__close" @click="showModal = false">&times;</div>
        <h3 class="connect-modal__title">{{ $t('home.connectModalTitle') }}</h3>
        <div class="connect-modal__options">
          <a class="connect-option" :href="facebookLoginPath">
            <svg viewBox="0 0 24 24" fill="#1877F2" width="40" height="40">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span>Facebook</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import photoMain from '../../../static/images/home/life-1.jpg'
import photoGrid1 from '../../../static/images/home/life-2.jpg'
import photoGrid2 from '../../../static/images/home/life-3.jpg'
import photoGrid3 from '../../../static/images/home/life-4.jpg'
import photoGrid4 from '../../../static/images/home/group-mother.png'
import config from 'src/app.config'

export default {
  name: 'HomeWhatIs',
  data () {
    return {
      activeSource: 0,
      timer: null,
      showModal: false,
      facebookLoginPath: config.facebookLoginPath,
      photoMain,
      photoGrid1,
      photoGrid2,
      photoGrid3,
      photoGrid4
    }
  },
  mounted () {
    this.timer = setInterval(() => {
      this.activeSource = (this.activeSource + 1) % 3
    }, 2000)
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    selectSource (source) {
      this.showModal = false
      this.$router.push({ name: 'shelf' })
    }
  }
}
</script>

<style lang="scss">
.what-is {
  padding: 5rem 1.5rem;
  background: #fff;

  &__visual {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    max-width: 900px;
    margin: 0 auto;
  }

  &__sources {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-shrink: 0;
  }

  &__transfer {
    position: relative;
    min-width: 180px;
    height: 220px;
    flex-shrink: 0;
  }

  &__book {
    flex-shrink: 0;
  }

  &__cta {
    text-align: center;
    margin-top: 2.5rem;
  }

  .btn-create {
    display: inline-block;
    padding: 0.875rem 3rem;
    background-color: $el-color-primary;
    color: #fff;
    font-size: 1.0625rem;
    font-weight: 600;
    border-radius: 100px;
    text-decoration: none;
    transition: all 0.25s;

    &:hover {
      background: linear-gradient(to right, #F56368, #ff878b);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(124, 58, 237, 0.4);
      color: #fff;
      text-decoration: none;
    }
  }
}

/* Source Icons */
.source-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.4s, box-shadow 0.4s, opacity 0.4s;
  opacity: 0.6;
  transform: scale(0.9);

  &.active {
    opacity: 1;
    transform: scale(1.1);
  }

  &--fb {
    background: #1877F2;
    box-shadow: 0 4px 16px rgba(24, 119, 242, 0.3);
    &.active { box-shadow: 0 6px 24px rgba(24, 119, 242, 0.5); }
  }

  &--ig {
    background: linear-gradient(135deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5);
    box-shadow: 0 4px 16px rgba(214, 41, 118, 0.3);
    &.active { box-shadow: 0 6px 24px rgba(214, 41, 118, 0.5); }
  }

  &--x {
    background: #000;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    &.active { box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4); }
  }
}

/* Curved Transfer Lines */
.transfer-curves {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.curve-path {
  fill: none;
}

.curve-active {
  fill: none;
  color: $el-color-primary;
  stroke-dasharray: 320;
  stroke-dashoffset: 320;

  &--1 { animation: curveTrace 3s ease-in-out infinite; }
  &--2 { animation: curveTrace 3s ease-in-out 0.4s infinite; }
  &--3 { animation: curveTrace 3s ease-in-out 0.8s infinite; }
}

@keyframes curveTrace {
  0% { stroke-dashoffset: 320; opacity: 0; }
  10% { opacity: 1; }
  50% { stroke-dashoffset: 0; opacity: 1; }
  70% { stroke-dashoffset: 0; opacity: 0.3; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}

/* Floating icons */
.curve-icon {
  position: absolute;
  width: 28px;
  height: 28px;
  background: #fff;
  border: 2px solid $el-color-primary;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $el-color-primary;
  box-shadow: 0 2px 8px rgba(81, 45, 202, 0.2);
  opacity: 0;

  &--photo {
    animation: iconCurve1 3s ease-in-out infinite;
  }
  &--video {
    animation: iconCurve2 3s ease-in-out 0.4s infinite;
  }
  &--text {
    animation: iconCurve3 3s ease-in-out 0.8s infinite;
  }
}

/* Top curve: start top-left, end center-right */
@keyframes iconCurve1 {
  0% { left: 0; top: 0; opacity: 0; }
  10% { opacity: 1; }
  50% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 0; }
}

/* Middle curve: straight across center */
@keyframes iconCurve2 {
  0% { left: 0; top: calc(50% - 14px); opacity: 0; }
  10% { opacity: 1; }
  50% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 0; }
}

/* Bottom curve: start bottom-left, end center-right */
@keyframes iconCurve3 {
  0% { left: 0; top: calc(100% - 28px); opacity: 0; }
  10% { opacity: 1; }
  50% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: calc(100% - 28px); top: calc(50% - 14px); opacity: 0; }
}

.transfer-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;

  span {
    font-size: 0.8125rem;
    color: $el-color-primary;
    font-weight: 600;
    line-height: 1.3;
  }
}

/* Open Book */
.open-book {
  display: flex;
  align-items: stretch;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.12));

  &__page {
    width: 180px;
    height: 240px;
    background: #fff;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &--left {
      border-radius: 6px 0 0 6px;
      border: 1px solid #e5e7eb;
      border-right: none;
    }

    &--right {
      border-radius: 0 6px 6px 0;
      border: 1px solid #e5e7eb;
      border-left: none;
    }
  }

  &__spine {
    width: 6px;
    background: linear-gradient(to right, #d1d5db, #e5e7eb, #d1d5db);
    flex-shrink: 0;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 1px;
      height: 100%;
      background: #c9cdd3;
    }
  }
}

.page-photo {
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &--main {
    flex: 1;
    min-height: 0;
  }

  &--grid {
    aspect-ratio: 1;
  }
}

.page-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  flex: 1;
  min-height: 0;
}

.page-text-lines {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex-shrink: 0;

  span {
    height: 3px;
    background: #e5e7eb;
    border-radius: 2px;
    width: 100%;

    &.short {
      width: 55%;
    }
  }
}

/* Desktop: show horizontal, hide vertical */
.what-is__transfer--mobile {
  display: none;
}

/* Vertical transfer curves (mobile) */
.transfer-curves-v {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.curve-active-v {
  fill: none;
  color: $el-color-primary;
  stroke-dasharray: 200;
  stroke-dashoffset: 200;

  &--1 { animation: curveTraceV 3s ease-in-out infinite; }
  &--2 { animation: curveTraceV 3s ease-in-out 0.4s infinite; }
  &--3 { animation: curveTraceV 3s ease-in-out 0.8s infinite; }
}

@keyframes curveTraceV {
  0% { stroke-dashoffset: 200; opacity: 0; }
  10% { opacity: 1; }
  50% { stroke-dashoffset: 0; opacity: 1; }
  70% { stroke-dashoffset: 0; opacity: 0.3; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}

.curve-icon-v {
  position: absolute;
  width: 24px;
  height: 24px;
  background: #fff;
  border: 2px solid $el-color-primary;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $el-color-primary;
  box-shadow: 0 2px 8px rgba(81, 45, 202, 0.2);
  opacity: 0;

  &--photo {
    animation: iconV1 3s ease-in-out infinite;
  }
  &--video {
    animation: iconV2 3s ease-in-out 0.4s infinite;
  }
  &--text {
    animation: iconV3 3s ease-in-out 0.8s infinite;
  }
}

/* Left curve: top-left → bottom-center */
@keyframes iconV1 {
  0% { left: 14%; top: 0; opacity: 0; }
  10% { opacity: 1; }
  50% { left: 45%; top: calc(100% - 24px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: 45%; top: calc(100% - 24px); opacity: 0; }
}

/* Center curve: top-center → bottom-center */
@keyframes iconV2 {
  0% { left: 45%; top: 0; opacity: 0; }
  10% { opacity: 1; }
  50% { left: 45%; top: calc(100% - 24px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: 45%; top: calc(100% - 24px); opacity: 0; }
}

/* Right curve: top-right → bottom-center */
@keyframes iconV3 {
  0% { left: 76%; top: 0; opacity: 0; }
  10% { opacity: 1; }
  50% { left: 45%; top: calc(100% - 24px); opacity: 1; }
  60% { opacity: 0; }
  100% { left: 45%; top: calc(100% - 24px); opacity: 0; }
}

@media (max-width: 768px) {
  .what-is {
    padding: 2.5rem 1rem;

    header {
      margin-bottom: 1.5rem;
    }

    &__visual {
      flex-direction: column;
      gap: 1rem;
      align-items: center;
    }

    &__sources {
      flex-direction: row;
      gap: 1rem;
    }

    &__transfer--desktop {
      display: none;
    }

    &__transfer--mobile {
      display: block;
      position: relative;
      width: 260px;
      height: 130px;
      margin: 0 auto;

      .transfer-label {
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        flex-direction: row;
        gap: 12px;
        font-size: 0.75rem;
        white-space: nowrap;
      }
    }

    &__book {
      transform: scale(1.05);
    }

    &__cta {
      margin-top: 1rem;
    }
  }

  .source-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .open-book {
    &__page {
      width: 150px;
      height: 195px;
      padding: 10px;
      gap: 6px;
    }

    .page-grid {
      gap: 4px;
    }
  }

  .btn-create {
    padding: 12px 28px;
    font-size: 0.95rem;
  }
}
</style>
